/**
 * Wanderlust Jewelry - engraving block (blocks/wj-engraving.liquid).
 * Inputs stay `disabled` until the shopper ticks the toggle, so an untouched block never posts an empty
 * "Engraving" property to the cart; the text input only gets its name when enabled for the same reason.
 * The preview is drawn by assets/wj-engrave-render.js (loaded on first open, so it costs nothing until used).
 * Engraving is paid (D-75): the live price is worked out by assets/wj-fee-calc.js from the text-engraving
 * product's live variants (data-text-fees), and assets/wj-fee-lines.js adds the fee lines next to the piece.
 */
class WjEngraving extends HTMLElement {
  connectedCallback() {
    // Horizon re-renders the product block on a variant change, which detaches and re-attaches this element:
    // register as a fee provider on every connect, not only the first (disconnectedCallback removes it).
    (window.WJFeeProviders ||= new Set()).add(this);
    if (this.onResize) window.addEventListener('resize', this.onResize);
    // Disabled inputs leave the product form (Buy it now posts them otherwise); also after a Horizon re-render
    this.formId ||= this.querySelector('[data-ref="text"]')?.getAttribute('form') || '';
    this.querySelectorAll('.wj-engraving__fields input:disabled').forEach((el) => el.removeAttribute('form'));
    if (this.ready) return;
    this.ready = true;
    const $ = (name) => this.querySelector(`[data-ref="${name}"]`);
    this.toggle = $('toggle');
    this.text = $('text');
    this.count = $('count');
    this.canvas = $('canvas');
    this.fields = this.querySelector('.wj-engraving__fields');

    // Image engraving (D-34): only rendered when the product allows it
    this.file = /** @type {HTMLInputElement | null} */ ($('file'));
    this.later = /** @type {HTMLInputElement | null} */ ($('later'));
    this.ref = /** @type {HTMLInputElement | null} */ ($('ref'));
    this.threshold = /** @type {HTMLInputElement | null} */ ($('threshold'));
    this.imgCanvas = $('imgcanvas');

    this.toggle.addEventListener('change', () => this.setEnabled(this.toggle.checked));
    this.text.addEventListener('input', () => this.update());
    this.file?.addEventListener('change', () => this.loadImage());
    this.later?.addEventListener('change', () => this.applyType());
    this.threshold?.addEventListener('input', () => this.imageRenderer?.set({ threshold: Number(this.threshold?.value) }));
    try {
      this.textFees = JSON.parse(this.dataset.textFees || '{}');
    } catch {
      this.textFees = {};
    }
    import(this.dataset.calc || new URL('./wj-fee-calc.js', import.meta.url).href).then((calc) => {
      this.calc = calc;
      this.update();
    });
    this.addEventListener('change', (event) => {
      const target = /** @type {HTMLInputElement} */ (event.target);
      if (target.type !== 'radio') return;
      if (target.name.includes('Engraving type')) {
        this.applyType();
        return;
      }
      if (target.name.includes('Engraving font')) this.renderer?.set({ font: target.value.toLowerCase() });
      if (target.name.includes('Engraving placement')) this.flip(target.dataset.side || 'inside');
      if (target.name.includes('Engraving size')) {
        this.renderer?.set({ size: target.value.toLowerCase() });
        this.update();
      }
    });
    this.addEventListener('click', (event) => {
      const symbol = /** @type {HTMLElement} */ (event.target).closest('[data-symbol]');
      if (symbol) {
        this.insertSymbol(symbol.dataset.symbol || '');
        return;
      }
      const button = /** @type {HTMLElement} */ (event.target).closest('[data-view]');
      if (!button) return;
      this.querySelectorAll('[data-view]').forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
      this.renderer?.set({ mode: button.dataset.view });
    });
    this.onResize = () => {
      if (!this.renderer || this.fields.hidden) return;
      this.renderer.resize();
      this.renderer.render();
    };
    window.addEventListener('resize', this.onResize);
    (window.WJFeeProviders ||= new Set()).add(this);
    // Size / variant changes re-price the 'With your ring' total
    document.addEventListener('shopify:product:select', () => setTimeout(() => this.showPrice(this.text.value), 50));
    // ...and so do quantity changes (typed or the +/- buttons)
    for (const type of ['change', 'input', 'click']) {
      document.addEventListener(type, (event) => {
        const t = /** @type {HTMLElement} */ (event.target);
        if (t && !this.contains(t)) setTimeout(() => this.showPrice(this.text.value), 50);
      });
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    window.WJFeeProviders?.delete(this);
  }

  /** @param {boolean} on */
  async setEnabled(on) {
    this.enabled = on;
    this.fields.hidden = !on;
    this.toggle.setAttribute('aria-expanded', String(on));
    // The delivery estimate drops the same-day promise and adds the engraving days (D-52, D-34).
    document.dispatchEvent(
      new CustomEvent('wj:engraving-change', { detail: { on, leadDays: Number(this.dataset.leadDays) || 0 } })
    );
    await this.applyType();
    if (on && this.type() === 'words') this.text.focus();
  }

  /** 'words' or 'image' (the chosen tab); blocks without image engraving are always 'words'. */
  type() {
    const checked = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="properties[Engraving type]"]:checked'));
    return checked?.dataset.type === 'image' ? 'image' : 'words';
  }

  /**
   * Shows the chosen panel and enables only its inputs (plus the type tabs and placement), so the cart only gets
   * the properties that apply: words never post an empty image, an image never posts an empty "Engraving".
   */
  async applyType() {
    const on = !!this.enabled;
    const type = this.type();
    this.querySelectorAll('[data-panel]').forEach((panel) => {
      /** @type {HTMLElement} */ (panel).hidden = panel.getAttribute('data-panel') !== type;
    });
    this.querySelectorAll('.wj-engraving__fields input').forEach((el) => {
      const input = /** @type {HTMLInputElement} */ (el);
      const panel = input.closest('[data-panel]');
      input.disabled = !on || (!!panel && panel.getAttribute('data-panel') !== type);
      // Shopify's Buy it now posts checked inputs even when disabled, so a disabled input also leaves the form
      if (input.disabled) input.removeAttribute('form');
      else if (this.formId) input.setAttribute('form', this.formId);
    });

    const words = on && type === 'words';
    if (words) {
      this.text.name = 'properties[Engraving]';
      this.text.required = true;
    } else {
      this.text.removeAttribute('name');
      this.text.required = false;
    }

    // Image engraving on stone-set or phase-2 rings is inside the band only
    const outside = /** @type {HTMLInputElement | null} */ (this.querySelector('input[data-side="outside"]'));
    const inside = /** @type {HTMLInputElement | null} */ (this.querySelector('input[data-side="inside"]'));
    if (outside && inside) {
      const insideOnly = type === 'image' && this.dataset.imageSides === 'inside';
      outside.closest('label')?.toggleAttribute('hidden', insideOnly);
      if (insideOnly && outside.checked) {
        inside.checked = true;
        this.flip('inside');
      }
    }

    if (this.file && this.later) {
      this.file.required = on && type === 'image' && !this.later.checked;
      if (this.ref && !this.ref.value) this.ref.value = `IMG-${Date.now().toString(36).toUpperCase()}`;
    }

    if (on && type === 'words') await this.ensureRenderer();
    if (on && type === 'image') await this.ensureImageRenderer();
    this.update();
  }

  async ensureImageRenderer() {
    if (this.imageRenderer || !this.imgCanvas) return;
    const url = this.dataset.imageRenderer || new URL('./wj-engrave-image.js', import.meta.url).href;
    const { ImageEngraveRenderer } = await import(url);
    const { metalFromText, METALS } = await import(this.dataset.renderer || './wj-engrave-render.js');
    this.imageRenderer = new ImageEngraveRenderer(/** @type {HTMLCanvasElement} */ (this.imgCanvas), {
      shape: this.dataset.shape,
      palette: METALS[metalFromText(this.dataset.metal)],
      threshold: Number(this.threshold?.value || 128),
    });
  }

  async loadImage() {
    const file = this.file?.files?.[0];
    const hint = this.querySelector('[data-ref="filehint"]');
    const row = this.querySelector('[data-ref="linesrow"]');
    if (!file) return;
    // A new image is a new engraving: its own reference to pair the piece with its fee line
    if (this.ref) this.ref.value = `IMG-${Date.now().toString(36).toUpperCase()}`;
    await this.ensureImageRenderer();
    try {
      await this.imageRenderer.load(file);
      if (hint) hint.hidden = true;
      if (row) /** @type {HTMLElement} */ (row).hidden = false;
    } catch {
      // HEIC and some camera formats can't be drawn in every browser; the file still uploads with the order
      if (hint) {
        hint.textContent = "We can't show a preview of this file on your device, but it will be sent with your order and we'll email you a proof.";
        hint.hidden = false;
      }
      if (row) /** @type {HTMLElement} */ (row).hidden = true;
    }
  }

  /**
   * Fee lines for /cart/add.js, asked for by assets/wj-fee-lines.js just before the piece goes into the bag
   * ("For" is added there). If the piece then fails to add, assets/wj-engrave-fee-guard.js removes the fees.
   * Words (D-84): ENGRAVE-TEXT-CHAR x characters and ENGRAVE-SYMBOL x symbols, variants by SKU; quantity =
   * per piece x pieces ("_Per piece" lets the guard keep that in step); same "_Fee ref" as the piece.
   * Image: the chosen type's fee product (fingerprint / paw print D-81, custom image D-59); same "_Image ref".
   * @param {{ ref: string, quantity: number }} context
   */
  feeItems({ ref, quantity }) {
    if (!this.enabled) return [];
    if (this.type() === 'image') {
      if (!this.file) return [];
      const chosen = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="properties[Engraving type]"]:checked'));
      const feeVariant = Number(chosen?.dataset.fee || this.dataset.feeVariant);
      if (!feeVariant) return [];
      // The reference stays as it is until a new image is chosen (loadImage), so the piece line Horizon builds
      // from this same form always carries the fee's _Image ref, however late it reads the form.
      return [{ id: feeVariant, quantity, properties: { '_Image ref': this.ref?.value } }];
    }
    if (!this.calc || !this.text.value.trim()) return [];
    return this.calc.engravingLines(this.text.value).flatMap((line) => {
      const fee = this.textFees[line.sku];
      if (!fee) return [];
      return [{ id: fee.id, quantity: line.quantity * quantity, properties: { '_Fee ref': ref, '_Per piece': String(line.quantity) } }];
    });
  }

  /** @param {string} message */
  feeError(message) {
    const hint = /** @type {HTMLElement | null} */ (this.querySelector(this.type() === 'image' ? '[data-ref="filehint"]' : '[data-ref="fee"]'));
    if (!hint) return;
    hint.textContent = message;
    hint.hidden = false;
  }

  async ensureRenderer() {
    if (this.renderer || !this.canvas) return;
    const url = this.dataset.renderer || new URL('./wj-engrave-render.js', import.meta.url).href;
    const { EngraveRenderer, metalFromText } = window.WJEngrave || (await import(url));

    let image = null;
    let area = null;
    if (this.dataset.photo && this.dataset.area) {
      try {
        area = JSON.parse(this.dataset.area);
        image = await new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = this.dataset.photo;
        });
      } catch {
        image = null; // fall back to the drawn view
      }
    }
    const checked = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="properties[Engraving font]"]:checked'));
    this.renderer = new EngraveRenderer(this.canvas, {
      mode: image ? this.dataset.mode : this.dataset.shape,
      metal: metalFromText(this.dataset.metal),
      font: (checked?.value || 'script').toLowerCase(),
      side: /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="properties[Engraving placement]"]:checked'))?.dataset.side || 'inside',
      size: (/** @type {HTMLInputElement | null} */ (this.querySelector('input[name="properties[Engraving size]"]:checked'))?.value || 'medium').toLowerCase(),
      image,
      area,
      placeholder: this.dataset.placeholder,
    });
    if (!image) this.querySelector('.wj-engraving__views')?.setAttribute('hidden', '');
  }

  /**
   * Turns the preview over to the other side of the band: a quick card flip, redrawn at the halfway point.
   * @param {'inside' | 'outside'} side
   */
  async flip(side) {
    const renderer = this.renderer;
    if (!renderer || renderer.side === side) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !this.canvas.animate) {
      renderer.set({ side });
      return;
    }
    const duration = 170;
    // Don't rely on `finished` alone: browsers pause animations in background tabs, so cap the wait.
    const play = (frames) => {
      const animation = this.canvas.animate(frames, { duration, easing: 'cubic-bezier(.4,0,.2,1)', fill: 'forwards' });
      return Promise.race([animation.finished.catch(() => {}), new Promise((r) => setTimeout(r, duration + 80))]);
    };
    this.canvas.classList.add('is-flipping');
    await play([{ transform: 'perspective(900px) rotateX(0deg)' }, { transform: 'perspective(900px) rotateX(90deg)' }]);
    await renderer.set({ side });
    await play([{ transform: 'perspective(900px) rotateX(-90deg)' }, { transform: 'perspective(900px) rotateX(0deg)' }]);
    this.canvas.getAnimations().forEach((a) => a.cancel());
    this.canvas.classList.remove('is-flipping');
  }

  /** Inserts a symbol (♥, ∞ …) at the cursor, within the character limit. @param {string} symbol */
  insertSymbol(symbol) {
    if (!symbol || this.text.disabled) return;
    const input = this.text;
    const max = this.maxChars();
    const count = this.calc ? this.calc.charCount : (/** @type {string} */ v) => [...v].length;
    if (count(input.value) + count(symbol) > max) {
      input.focus();
      return;
    }
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    input.value = input.value.slice(0, start) + symbol + input.value.slice(end);
    const caret = start + symbol.length;
    input.focus();
    input.setSelectionRange(caret, caret);
    this.update();
  }

  /**
   * Character limit in characters, not UTF-16 units, so a symbol like 🐾 counts as one: the smaller of the site
   * cap (22, D-75) and the product's own custom.engraving_max_chars (the Liquid has already taken the smaller).
   */
  /** Price (cents) of the variant currently selected in the product form, or null. */
  piecePrice() {
    let prices = {};
    try {
      prices = JSON.parse(this.dataset.variantPrices || '{}');
    } catch {
      return null;
    }
    const input = /** @type {HTMLInputElement | null} */ (document.querySelector(`#${CSS.escape(this.formId)} [name="id"]`) || document.querySelector(`[name="id"][form="${this.formId}"]`));
    const id = input?.value || this.dataset.firstVariant || '';
    const price = prices[id];
    return typeof price === 'number' ? price : null;
  }

  maxChars() {
    const own = Number(this.text.dataset.max) || 0;
    if (this.calc) return this.calc.maxChars(own);
    return own > 0 ? Math.min(own, 22) : 22;
  }

  update() {
    const max = this.maxChars();
    const chars = this.calc ? this.calc.graphemes(this.text.value) : [...this.text.value];
    if (chars.length > max) this.text.value = chars.slice(0, max).join('');
    const value = this.text.value;
    const used = Math.min(chars.length, max);
    this.count.textContent = `${used}/${max}`;
    this.count.setAttribute('aria-label', `${max - used} of ${max} characters left`);
    this.renderer?.set({ text: value });
    this.showPrice(value);
  }

  /**
   * The live price under the box, from the live variant prices (D-84):
   * "Engraving R1,196: 11 characters × R92 + 1 symbol × R184 (spaces are free)".
   * @param {string} value
   */
  showPrice(value) {
    const out = /** @type {HTMLElement | null} */ (this.querySelector('[data-ref="fee"]'));
    const calc = this.calc;
    if (!out || !calc) return;
    const lines = calc.engravingLines(value);
    if (!lines.length) {
      out.textContent = out.dataset.empty || '';
      return;
    }
    const total = calc.linesTotal(lines, this.textFees);
    if (total === null) {
      out.textContent = '';
      return;
    }
    const parts = lines.map((line) => {
      const unit = calc.formatMoney(this.textFees[line.sku].price);
      const n = line.quantity;
      const noun = line.sku === 'ENGRAVE-SYMBOL' ? `symbol${n > 1 ? 's' : ''}` : `character${n > 1 ? 's' : ''}`;
      return `${n} ${noun} × ${unit}`;
    });
    let text = `Engraving ${calc.formatMoney(total)}: ${parts.join(' + ')} (spaces are free)`;
    // Justin, 9 Oct 2026: show the total with the piece, not only the engraving on its own
    const piece = this.piecePrice();
    if (piece !== null) {
      // Engraving is charged per piece, so the total follows the quantity box
      const qtyInput = /** @type {HTMLInputElement | null} */ (
        document.querySelector(`#${CSS.escape(this.formId)} [name="quantity"]`) || document.querySelector(`[name="quantity"][form="${this.formId}"]`)
      );
      const qty = Math.max(1, Number(qtyInput?.value) || 1);
      const noun = this.dataset.pieceNoun || 'piece';
      const label = qty > 1 ? `With your ${qty} ${noun}s` : `With your ${noun}`;
      text += ` · ${label}: ${calc.formatMoney((piece + total) * qty)}`;
    }
    out.textContent = text;
  }
}

if (!customElements.get('wj-engraving')) customElements.define('wj-engraving', WjEngraving);
